import React, { useState, useEffect, useRef } from 'react'
import { useRefAsState } from 'modules/React.Extensions'

export const useGridDataSync = ({
    data,
    episodeId,
    draftAvailable,
    id,
    dataKey,
    filters,
    editingRowKey
}) => {
    const [localData, setLocalData] = useState([...data])

    // Use refs to track previous values to avoid state in useEffect dependencies
    const prevEpisodeId = useRef(episodeId)
    const prevDraftAvailable = useRef(draftAvailable)
    const prevDataRef = useRef(data)
    const localDataRef = useRef(localData)
    localDataRef.current = localData

    // Contributors need to be refreshed when a different episode is opened
    useEffect(() => {
        if (episodeId !== prevEpisodeId.current) {
            setLocalData(data)
            prevEpisodeId.current = episodeId
        }
    }, [episodeId, data])

    // Refresh when draft is cleared (e.g., Refresh button pressed)
    useEffect(() => {
        if (draftAvailable !== prevDraftAvailable.current) {
            prevDraftAvailable.current = draftAvailable
            if (draftAvailable === false) {
                setLocalData(data)
            }
        }
    }, [draftAvailable, data])

    // Sync localData with data prop when not actively editing a row
    // This allows external updates (like Auto %) to be reflected in the grid
    // Preserves unsaved new rows (rows in localData that don't exist in data)
    // BUT only when draftAvailable is true (not after explicit Refresh)
    useEffect(() => {
        // Only sync if data actually changed and no row is being edited
        if (data !== prevDataRef.current && !editingRowKey) {
            // Only preserve unsaved rows if draft is available (not after Refresh)
            if (draftAvailable) {
                // Find new rows that exist only in localData (not yet saved to Redux)
                // Use ref to avoid localData in dependencies (prevents extra rerenders)
                const unsavedNewRows = localDataRef.current.filter(
                    localRow => !localRow.isDeleted && !data.some(dataRow => dataRow[dataKey] === localRow[dataKey])
                )
                // Preserve unsaved new rows at the top, then add synced data
                setLocalData([...unsavedNewRows, ...data])
            } else {
                // After Refresh, don't preserve unsaved rows
                setLocalData([...data])
            }
        }
        prevDataRef.current = data
    }, [data, editingRowKey, dataKey, draftAvailable])

    const preprocessEmptyIdaCode = (data, allFilters) => {
        let filteredData = data
        
        // Process all filters for __EMPTY__ placeholder and empty value handling
        if (allFilters) {
            Object.keys(allFilters).forEach(fieldName => {
                const fieldFilter = allFilters[fieldName]
                if (fieldFilter?.constraints?.length > 0) {
                    const filterConstraint = fieldFilter.constraints[0]
                    const filterMatchMode = filterConstraint.matchMode
                    const filterValue = filterConstraint.value
                    
                    if (filterValue != null) {
                        const normalizedFilterValue = filterValue?.toString()?.trim()
                        
                        filteredData = filteredData.filter(item => {
                            if (!(fieldName in item)) return true // Field doesn't exist, include item
                            
                            const fieldValue = item[fieldName]
                            
                            // Handle __EMPTY__ placeholder - match null/empty values
                            if (filterMatchMode === 'equals' && normalizedFilterValue === '__EMPTY__') {
                                const isEmpty = fieldValue === null || 
                                       fieldValue === undefined || 
                                       fieldValue === '' || 
                                       fieldValue === '-' ||
                                       (typeof fieldValue === 'string' && fieldValue.trim() === '')
                                console.log(`Empty filter for ${fieldName}:`, { fieldValue, isEmpty })
                                return isEmpty
                            }
                            
                            // Handle empty string filter for idaCode (legacy behavior)
                            if (fieldName === 'idaCode' && filterMatchMode === 'equals' && normalizedFilterValue === '') {
                                return fieldValue === null || 
                                       fieldValue === undefined || 
                                       fieldValue === '' || 
                                       fieldValue === 0 || 
                                       (typeof fieldValue === 'string' && fieldValue.trim() === '')
                            }
                            
                            // Standard equality check
                            if (filterMatchMode === 'equals') {
                                return fieldValue === filterValue
                            }
                            
                            return true
                        })
                    }
                }
            })
        }
        
        return filteredData
    }

    // Apply filter preprocessing in useEffect to avoid render-time side effects
    useEffect(() => {
        if (filters) {
            const newLocalData = preprocessEmptyIdaCode([...data], filters)
            if (JSON.stringify(newLocalData) !== JSON.stringify(localData)) {
                setLocalData(newLocalData)
            }
        }
    }, [filters, data])

    // Remove __EMPTY__ filters from the filters object since we handle them in preprocessing
    // This prevents PrimeReact from applying its own filter on top of our preprocessed data
    const sanitizedFilters = React.useMemo(() => {
        if (!filters) return null
        
        const sanitized = { ...filters }
        Object.keys(sanitized).forEach(fieldName => {
            const fieldFilter = sanitized[fieldName]
            if (fieldFilter?.constraints?.length > 0) {
                // Check if any constraint has __EMPTY__ value
                const updatedConstraints = fieldFilter.constraints.map(constraint => {
                    if (constraint.value === '__EMPTY__') {
                        // Keep the constraint but change to a value that won't interfere
                        // Use null so PrimeReact won't apply additional filtering
                        return { ...constraint, value: null }
                    }
                    return constraint
                })
                
                sanitized[fieldName] = {
                    ...fieldFilter,
                    constraints: updatedConstraints
                }
            }
        })
        
        return sanitized
    }, [filters])

    const [selectedChannels, setSelectedChannels] = useRefAsState(() => {
        const initialSelectedChannels = {}
        localData.forEach((rowData) => {
            initialSelectedChannels[rowData[dataKey]] = rowData.channel?.name || null
        })
        return initialSelectedChannels
    })

    return {
        localData,
        setLocalData,
        selectedChannels,
        setSelectedChannels,
        sanitizedFilters
    }
}
